import { CircularIconButton } from '#app/components/Buttons/CircularIconButton.js';
import { type ButtonSize } from '#app/components/Buttons/types.js';
import { AddToListMenu } from '#app/components/Network/AddToListMenu/AddToListMenu.js';
import { UnauthenticatedAddToListMenu } from '#app/components/Network/AddToListMenu/UnauthenticatedAddToListMenu.js';
import { useAddToListMenu } from '#app/components/Network/AddToListMenu/useAddToListMenu.js';
import { useOnboardingRedirection } from '#app/components/RedirectToOnboardingModal/useOnboardingRedirection.js';
import { Tooltip } from '#app/components/Tooltip/Tooltip.js';
import { useCurrentUser } from '#app/hooks/useCurrentUser.js';
import { useFeatureFlag } from '#app/hooks/useFeatureFlag.js';
import { useIsMobile } from '#app/hooks/useIsMobile.js';
import { useLazyLoadedRoles } from '#app/hooks/useLazyLoadedRoles.js';
import { useLazyLoadedTools } from '#app/hooks/useLazyLoadedTools.js';
import { useLocation } from '#app/hooks/useLocation.js';
import { CreateContactForUserProfileSource } from '@contra/graphql-types';
import {
  BookmarkOutlineIcon,
  BookmarkSolidIcon,
  UserAddOutlineIcon,
  UserCheckSolidIcon,
} from '@contra/icons/icons';
import { isTruthy } from '@contra/utilities/isTruthy';
import { Suspense, useCallback, useEffect, useState } from 'react';

type SaveUserButtonProps = SaveUserButtonV1Props & SaveUserButtonV2Props;

type SaveUserButtonV1Props = {
  readonly addContact: () => void;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly removeContact: () => void;
  readonly size?: ButtonSize;
  readonly userName: null | string;
};

type SaveUserButtonV2Props = {
  readonly analyticsSection: string;
  readonly appliedFilters?: {
    roleIds: string[];
    toolIds: string[];
  };
  readonly contactId: null | string;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly size?: ButtonSize;
  readonly source: CreateContactForUserProfileSource;
  readonly userProfile: {
    avatar: null | {
      id: string;
      uid: string;
      url: string;
    };
    firstName: string;
    id: string;
    isSavedContact: boolean;
  };
};

const SaveUserButtonV1 = ({
  addContact,
  hideBorder,
  isSavedContact,
  removeContact,
  size,
  userName,
}: SaveUserButtonV1Props) => {
  const location = useLocation();
  const isMobile = useIsMobile();

  const { handleOnboardingRedirectionModalOpen, shouldRedirectUser } =
    useOnboardingRedirection({
      overrideUserType: 'client',
      redirectTo: location.path,
    });

  const onSaveClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (shouldRedirectUser) {
        handleOnboardingRedirectionModalOpen();
      } else {
        addContact();
      }
    },
    [addContact, handleOnboardingRedirectionModalOpen, shouldRedirectUser],
  );

  const onRemoveClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      event.stopPropagation();
      event.preventDefault();
      if (shouldRedirectUser) {
        handleOnboardingRedirectionModalOpen();
      } else {
        removeContact();
      }
    },
    [handleOnboardingRedirectionModalOpen, removeContact, shouldRedirectUser],
  );

  const button = isSavedContact ? (
    <CircularIconButton
      aria-label={`${userName ?? 'This user'} is in your network`}
      icon={UserCheckSolidIcon}
      onClick={onRemoveClick}
      size={(size ?? isMobile) ? 'large' : 'small'}
      variant={hideBorder ? 'V2Link' : 'V2TertiaryOutline'}
    />
  ) : (
    <CircularIconButton
      aria-label={`Add ${userName ?? 'this user'} to your network`}
      icon={UserAddOutlineIcon}
      onClick={onSaveClick}
      size={(size ?? isMobile) ? 'large' : 'small'}
      variant={hideBorder ? 'V2Link' : 'V2TertiaryOutline'}
    />
  );

  if (isSavedContact) {
    return button;
  }

  return (
    <Tooltip
      content={`Add ${userName ?? 'this user'} to network. They will not be notified.`}
      width="auto"
    >
      {button}
    </Tooltip>
  );
};

const SaveButtonV2 = ({
  firstName,
  hideBorder,
  isSavedContact,
  onClick,
  size,
}: {
  readonly firstName: string;
  readonly hideBorder?: boolean;
  readonly isSavedContact: boolean;
  readonly onClick: () => void;
  readonly size: ButtonSize;
}) => {
  return (
    <Tooltip
      content={isSavedContact ? 'Saved' : 'Save'}
      hideReferenceContainerOutlineOnFocus
      width="auto"
    >
      <CircularIconButton
        aria-label={
          isSavedContact ? `${firstName} is saved` : `Save ${firstName}`
        }
        icon={isSavedContact ? BookmarkSolidIcon : BookmarkOutlineIcon}
        onClick={onClick}
        size={size}
        variant={
          hideBorder
            ? isSavedContact
              ? 'V2BrandPurpleBorderless'
              : 'V2TertiaryBorderless'
            : isSavedContact
              ? 'V2BrandPurpleBorderless'
              : 'V2TertiaryOutline'
        }
      />
    </Tooltip>
  );
};

const basePlaceholderListName = `Talent for future projects 🔮`;

const LazyPlaceholderListNameFetcher = ({
  appliedFilters,
  setListName,
}: {
  readonly appliedFilters: {
    roleIds: string[];
    toolIds: string[];
  };
  readonly setListName: (listName: string) => void;
}) => {
  const roles = useLazyLoadedRoles();
  const { suggestedTools: tools } = useLazyLoadedTools({
    skipFetchPopularTools: true,
  });

  const appliedRoles = appliedFilters.roleIds
    .map((roleId) => roles.find((role) => role.id === roleId))
    .filter(isTruthy)
    .map((role) => ({
      ...role,
      type: 'Role' as const,
    }));

  const appliedTools = appliedFilters.toolIds
    .map((toolId) => tools.find((tool) => tool.id === toolId))
    .filter(isTruthy)
    .map((tool) => ({
      ...tool,
      type: 'Tool' as const,
    }));

  if (appliedRoles.length > 0) {
    setListName(`${appliedRoles[0].name}s for future projects 🔮`);

    return null;
  }

  if (appliedTools.length > 0) {
    setListName(`${appliedTools[0].name} freelancers for future projects 🔮`);

    return null;
  }

  return null;
};

const SaveUserButtonV2 = ({
  analyticsSection,
  appliedFilters,
  contactId,
  hideBorder,
  isSavedContact,
  size = 'small',
  source,
  userProfile,
}: SaveUserButtonV2Props) => {
  const currentUser = useCurrentUser();

  const addToListMenuProps = useAddToListMenu({
    analyticsSection,
    isSaved: isSavedContact,
  });

  // want to optimistically mark the button as saved, so that it appears as checked as soon as a list is selected.
  const [optimisticIsSaved, setOptimisticIsSaved] = useState(isSavedContact);

  useEffect(() => {
    setOptimisticIsSaved(isSavedContact);
  }, [isSavedContact]);

  const trigger = (
    <SaveButtonV2
      firstName={userProfile.firstName}
      hideBorder={hideBorder}
      isSavedContact={optimisticIsSaved}
      onClick={() => {
        if (isSavedContact) {
          if (addToListMenuProps.isActionsMenuOpen) {
            addToListMenuProps.handleCloseActionsMenu();
          } else {
            addToListMenuProps.handleOpenActionsMenu();
          }
        } else {
          addToListMenuProps.handleOpenListMenu();
        }
      }}
      size={size}
    />
  );

  const [placeholderListName, setPlaceholderListName] = useState<string>(
    basePlaceholderListName,
  );

  if (!currentUser) {
    return (
      <>
        {appliedFilters ? (
          <Suspense>
            <LazyPlaceholderListNameFetcher
              appliedFilters={appliedFilters}
              setListName={setPlaceholderListName}
            />
          </Suspense>
        ) : null}

        <UnauthenticatedAddToListMenu
          addToListMenuProps={addToListMenuProps}
          analyticsSection={analyticsSection}
          placeholderListName={placeholderListName}
          source={source}
          trigger={trigger}
          userProfile={userProfile}
        />
      </>
    );
  }

  return (
    <AddToListMenu
      addToListMenuProps={addToListMenuProps}
      analyticsSection={analyticsSection}
      contactId={contactId}
      onListChecked={() => {
        setOptimisticIsSaved(true);
      }}
      source={source}
      trigger={trigger}
      userProfile={userProfile}
    />
  );
};

export const SaveUserButton = (args: SaveUserButtonProps) => {
  const reworkedSaveToListsFlow = useFeatureFlag('reworkedSaveToListsFlow');

  if (reworkedSaveToListsFlow) {
    return <SaveUserButtonV2 {...args} />;
  }

  return <SaveUserButtonV1 {...args} />;
};
